/**
 * LegalPage — Phase O.7 (patrol-web migration). Replaces the prior
 * Phase 4 `components/MarketingLegalPage.jsx` wrapper. Composes
 * `@puffin-core/legal-content`'s `<LegalDocumentPage>` with the canonical
 * `<MarketingSeo>` head + JSON-LD + page-view analytics.
 *
 * @module portals/marketing-website/pages/LegalPage
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getBrand } from '@puffin-core/brand-assets';
import {
  LEGAL_DOCUMENT_LANGUAGE,
  LegalDocumentPage,
  useLegalDocument,
} from '@puffin-core/legal-content';
import { MarketingSeo, useAnalytics, webPage } from '@puffin-core/marketing-shell';
import { PATROL_MARKETING_ROUTES } from '../constants/patrolMarketingRoutePaths';

const APP_KEY = 'patrol';

/**
 * @param {Object} props
 * @param {('privacy'|'terms')} props.documentKey
 * @returns {React.JSX.Element}
 */
export default function LegalPage({ documentKey }) {
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();
  const brand = getBrand(APP_KEY);
  const { document: doc } = useLegalDocument({ appKey: APP_KEY, documentKey });

  // The whole SEO surface derives from the fetched document, so the memo is
  // keyed directly on `doc` (+ `brand`/`documentKey`) rather than on
  // intermediate consts — same recompute semantics, explicit hook dependency.
  const seo = useMemo(() => {
    const docTitle = doc?.frontmatter?.title;
    const productName = doc?.frontmatter?.productName;
    const pageTitle = doc ? `${docTitle} - ${productName}` : 'Legal Document - PatrolPuffin';
    const description = doc?.frontmatter?.description || '';
    const canonicalUrl =
      doc?.frontmatter?.canonicalUrl || `https://www.patrolpuffin.com/${documentKey}`;
    return {
      pageTitle,
      description,
      canonicalUrl,
      jsonLd: [
        webPage({
          appKey: APP_KEY,
          brand,
          url: canonicalUrl,
          title: pageTitle,
          description,
        }),
      ],
    };
  }, [brand, doc, documentKey]);

  useEffect(() => {
    trackPageView(`/${documentKey}`, seo.pageTitle);
  }, [trackPageView, documentKey, seo.pageTitle]);

  const ogImageUrl = `https://www.patrolpuffin.com${brand.assets.ogCard}`;

  return (
    <div className="legal-page-container">
      <MarketingSeo
        title={seo.pageTitle}
        description={seo.description}
        canonical={seo.canonicalUrl}
        ogType="article"
        ogImage={ogImageUrl}
        jsonLd={seo.jsonLd}
        inLanguage={LEGAL_DOCUMENT_LANGUAGE}
      />

      <LegalDocumentPage appKey={APP_KEY} documentKey={documentKey} />

      <div className="legal-page-footer">
        <Link to={PATROL_MARKETING_ROUTES.PRIVACY} className="legal-cross-link">
          {t('footer.privacy')}
        </Link>
        <Link to={PATROL_MARKETING_ROUTES.TERMS} className="legal-cross-link">
          {t('footer.terms')}
        </Link>
      </div>
    </div>
  );
}

LegalPage.propTypes = {
  documentKey: PropTypes.oneOf(['privacy', 'terms']).isRequired,
};
